import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, Chip, EmptyState, SearchInput, Button } from '../ds/index'
import { useApi } from '../lib/useApi'
import { num } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { NewClient } from './Subscribers'

type Client = {
  id: string; name: string; status: 'active' | 'paused'; category_name: string; website: string | null; phone: string | null
  pitch: string | null; about: string | null; services: string | null; good_fit: string | null; not_a_fit: string | null
  profile_more: { key: string; label: string; value: string }[]
  areas: string[]; groups: string[]; my_leads_30d: number
}

/**
 * The clients a rep represents, to read before recommending one. A list, not a
 * table: reps use this on a phone. ?c=<id> opens that client, which is how a
 * lead links here.
 */
export default function MyClients() {
  const { data, error, reload } = useApi<{ clients: Client[] }>('/api/my/clients')
  const [adding, setAdding] = useState(false)
  const [params] = useSearchParams()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<string | null>(params.get('c'))

  useEffect(() => {
    const c = params.get('c')
    if (c) { setOpen(c); requestAnimationFrame(() => document.getElementById(`client-${c}`)?.scrollIntoView({ block: 'start' })) }
  }, [params, data])

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const needle = q.trim().toLowerCase()
  const shown = needle
    ? data.clients.filter((c) => [c.name, c.category_name, c.services, c.about, ...c.areas].filter(Boolean).join(' ').toLowerCase().includes(needle))
    : data.clients

  return (
    <>
      <PageHeader title="Clients you represent" subtitle="The businesses you recommend. Read up on one before you reply, so you can say what they do and why they fit."
                  actions={<Button onClick={() => setAdding(true)}>Add client</Button>} />
      {adding && <NewClient forRep onClose={() => setAdding(false)} onCreated={reload} />}
      {data.clients.length > 6 && (
        <div style={{ marginBottom: 'var(--s-5)' }}>
          <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, service or area" aria-label="Search your clients" />
        </div>
      )}
      {data.clients.length === 0 ? (
        <EmptyState title="No clients yet" body="Add a business you have signed up, or your admin assigns them. They show up here with what each one does." />
      ) : shown.length === 0 ? (
        <EmptyState title="No match" body="Nothing in your clients matches that search." />
      ) : (
        <ul className="client-list">
          {shown.map((c) => {
            const isOpen = open === c.id
            return (
              <li key={c.id} id={`client-${c.id}`} className={isOpen ? 'open' : undefined}>
                <button type="button" className="client-head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : c.id)}>
                  <span className="client-name">{c.name}</span>
                  <span className="client-meta">
                    <Chip>{c.category_name}</Chip>
                    {c.status === 'paused' && <Chip tone="warn">Paused</Chip>}
                    <span className="muted">{num(c.my_leads_30d)} lead{c.my_leads_30d === 1 ? '' : 's'} from you in 30 days</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="client-body">
                    {c.status === 'paused' && <p className="muted" style={{ margin: 0 }}>Paused: don't recommend them until your admin turns them back on.</p>}
                    {c.pitch && <Section label="What to say about them">{c.pitch}</Section>}
                    {c.about && <Section label="About">{c.about}</Section>}
                    {c.services && <Section label="Services">{c.services}</Section>}
                    {c.good_fit && <Section label="A good fit for">{c.good_fit}</Section>}
                    {c.not_a_fit && <Section label="Not a fit for">{c.not_a_fit}</Section>}
                    {c.profile_more.map((l) => <Section key={l.key} label={l.label}>{l.value}</Section>)}
                    {c.areas.length > 0 && <Section label="Areas">{c.areas.join(', ')}</Section>}
                    {c.groups.length > 0 && <Section label="Groups">{c.groups.join(', ')}</Section>}
                    {(c.phone || c.website) && (
                      <Section label="Contact to share">
                        {c.phone && <span>{c.phone}</span>}
                        {c.phone && c.website && ' · '}
                        {c.website && <a href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer">{c.website}</a>}
                      </Section>
                    )}
                    {!c.about && !c.services && !c.good_fit && !c.not_a_fit && c.profile_more.length === 0 && (
                      <p className="muted" style={{ margin: 0 }}>No profile yet. The client fills it in from their portal, or your admin can on Subscribers.</p>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="client-section">
      <div className="s-label muted">{label}</div>
      <div className="client-text">{children}</div>
    </div>
  )
}
